// Local-only AI debug panel. Renders a SearchReport (from SearchAI) into
// a side panel so we can see what the AI considered, what it picked, and
// why. Gated by main.ts on a URL param so production builds never mount
// it.
//
// The render core is a pure function — `renderSearchReport(container,
// report)` — so a future checkpoint-debug tool can call it directly
// without inheriting any of main.ts's runtime state. The mount helper
// (`mountAIDebugPanel`) layers a session history + Back/Forward nav on
// top of that pure render: every report pushed via `update()` is
// retained for the life of the page so we can scroll back through the
// AI's full decision trail during a debugging session.

import type { CardDef, GameAction } from '../types.js';
import type { SearchReport } from '../ai/types.js';

const PANEL_ID = 'ai-debug-panel';
const NAV_ID = 'ai-debug-nav';
const BODY_ID = 'ai-debug-body';
const HEADER_ID = 'ai-debug-header';
const CONTENT_ID = 'ai-debug-content';
const MAX_ACTIONS_SHOWN = 12;
/* Show the full principal variation. Lines can run a dozen+ plies deep
   (an attack spawns the opponent's block/confirm response, then both
   sides' subsequent turns), and truncating them hides exactly the moves
   that explain horizon-sensitive scoring — e.g. why SKIP can outscore an
   attack because the search saw a later beneficial play in the skip line.
   Large enough to never elide a real PV; the array is short regardless. */
const PV_HEAD = 64;

/** Mount a fixed-position side panel into <body>. Returns a handle the
 *  caller uses to push updates. Safe to call once at init.
 *
 *  The panel keeps an in-memory history of every SearchReport pushed via
 *  `update()`. Back/Forward buttons in the nav bar walk the history. If
 *  the user is currently viewing the latest entry when a new report
 *  arrives, the view auto-advances ("tail mode"); if they've scrolled
 *  back to inspect an old decision, the new entry stacks up but the
 *  view stays put. */
export function mountAIDebugPanel(catalog: Map<string, CardDef>): {
  update: (report: SearchReport | null) => void;
} {
  let panel = document.getElementById(PANEL_ID);
  if (!panel) {
    panel = document.createElement('aside');
    panel.id = PANEL_ID;
    Object.assign(panel.style, {
      position: 'fixed',
      top: '8px',
      right: '8px',
      width: '380px',
      maxHeight: 'calc(100vh - 16px)',
      overflowY: 'auto',
      padding: '4px 8px 8px',
      background: 'rgba(20,22,28,0.92)',
      color: '#e8e8ea',
      border: '1px solid #444',
      borderRadius: '6px',
      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
      fontSize: '11px',
      lineHeight: '1.4',
      zIndex: '9999',
      pointerEvents: 'auto',
    });
    panel.innerHTML = `
      <div id="${HEADER_ID}" style="display:flex;justify-content:flex-end;align-items:center">
        <button data-action="toggle" aria-label="Collapse AI debug" style="background:#2a2d33;color:#e8e8ea;border:1px solid #555;border-radius:3px;padding:1px 10px;font-family:inherit;font-size:12px;cursor:pointer;min-width:48px;min-height:24px;touch-action:manipulation">AI ▾</button>
      </div>
      <div id="${CONTENT_ID}">
        <div id="${NAV_ID}" style="display:flex;align-items:center;gap:8px;margin:4px 0 0;padding-bottom:6px;border-bottom:1px solid #333">
          <button data-action="prev" style="background:#2a2d33;color:#e8e8ea;border:1px solid #555;border-radius:3px;padding:2px 8px;font-family:inherit;font-size:11px;cursor:pointer">← prev</button>
          <button data-action="next" style="background:#2a2d33;color:#e8e8ea;border:1px solid #555;border-radius:3px;padding:2px 8px;font-family:inherit;font-size:11px;cursor:pointer">next →</button>
          <span data-role="indicator" style="opacity:0.7">0/0</span>
          <button data-action="latest" style="margin-left:auto;background:#2a2d33;color:#e8e8ea;border:1px solid #555;border-radius:3px;padding:2px 8px;font-family:inherit;font-size:11px;cursor:pointer">latest</button>
        </div>
        <div id="${BODY_ID}" style="margin-top:6px"></div>
      </div>
    `;
    document.body.appendChild(panel);
  }

  const body = panel.querySelector<HTMLElement>(`#${BODY_ID}`)!;
  const content = panel.querySelector<HTMLElement>(`#${CONTENT_ID}`)!;
  const toggleBtn = panel.querySelector<HTMLButtonElement>('[data-action="toggle"]')!;
  const indicator = panel.querySelector<HTMLElement>('[data-role="indicator"]')!;
  const prevBtn = panel.querySelector<HTMLButtonElement>('[data-action="prev"]')!;
  const nextBtn = panel.querySelector<HTMLButtonElement>('[data-action="next"]')!;
  const latestBtn = panel.querySelector<HTMLButtonElement>('[data-action="latest"]')!;

  const history: SearchReport[] = [];
  // -1 = no reports yet. Otherwise index into `history`. Tracked
  // separately from history.length so the user can hold a position
  // while new reports keep arriving in the background.
  let cursor = -1;
  // Persists across update() calls so collapsing once stays collapsed
  // for the rest of the session — important on phones where the panel
  // would otherwise cover the play area whenever the AI moves.
  let collapsed = false;

  const refresh = (): void => {
    const report = cursor >= 0 ? history[cursor] : null;
    renderSearchReport(body, report, catalog);
    const total = history.length;
    const shown = cursor + 1;
    const onLatest = cursor === total - 1;
    indicator.textContent = total === 0 ? '0/0' : `${shown}/${total}${onLatest ? ' (live)' : ''}`;
    prevBtn.disabled = cursor <= 0;
    nextBtn.disabled = cursor >= total - 1;
    latestBtn.disabled = onLatest || total === 0;
    // Visually de-emphasise disabled buttons since the default browser
    // style is barely distinguishable on dark backgrounds.
    for (const btn of [prevBtn, nextBtn, latestBtn]) {
      btn.style.opacity = btn.disabled ? '0.35' : '1';
      btn.style.cursor = btn.disabled ? 'default' : 'pointer';
    }
  };

  prevBtn.addEventListener('click', () => {
    if (cursor > 0) { cursor--; refresh(); }
  });
  nextBtn.addEventListener('click', () => {
    if (cursor < history.length - 1) { cursor++; refresh(); }
  });
  latestBtn.addEventListener('click', () => {
    if (history.length > 0) { cursor = history.length - 1; refresh(); }
  });

  const applyCollapsedState = (): void => {
    if (collapsed) {
      content.style.display = 'none';
      panel!.style.width = 'auto';
      // Drop the bottom padding when there's no content below the button.
      panel!.style.padding = '4px 8px';
      toggleBtn.textContent = 'AI ▸';
      toggleBtn.setAttribute('aria-label', 'Expand AI debug');
    } else {
      content.style.display = '';
      panel!.style.width = '380px';
      panel!.style.padding = '4px 8px 8px';
      toggleBtn.textContent = 'AI ▾';
      toggleBtn.setAttribute('aria-label', 'Collapse AI debug');
    }
  };
  toggleBtn.addEventListener('click', () => {
    collapsed = !collapsed;
    applyCollapsedState();
  });
  applyCollapsedState();

  refresh();

  return {
    update(report) {
      if (!report) return;
      const wasOnLatest = cursor === history.length - 1;  // also true when history is empty (cursor === -1, length === 0)
      history.push(report);
      if (wasOnLatest) cursor = history.length - 1;
      refresh();
    },
  };
}

/** Render a SearchReport into the supplied container. Pure: no globals,
 *  no fetch, no side effects beyond container.innerHTML. The checkpoint
 *  debug tool will call this directly.
 *
 *  Card names appear inline the first time each instanceId is mentioned
 *  within the rendered report (so the reader can connect the opaque
 *  `ci_82` to "Desert Nomad" without scrolling away). Subsequent
 *  mentions show only the id to keep the log compact. The "first time"
 *  scope is the rendered report, not the session — re-rendering or
 *  navigating to another report resets the seen set. */
export function renderSearchReport(
  container: HTMLElement,
  report: SearchReport | null,
  catalog: Map<string, CardDef>,
): void {
  if (!report) {
    container.innerHTML = '<div style="opacity:0.6">AI debug: waiting for next AI move…</div>';
    return;
  }

  const header = [
    `<div style="font-weight:600;margin-bottom:4px">AI debug — profile <code>${escapeHtml(report.profileId)}</code></div>`,
    `<div style="opacity:0.75;margin-bottom:8px">depth=${report.searchDepth}  nodes=${report.nodesExplored}  time=${report.searchTimeMs}ms  rootScore=${fmtScore(report.rootScore)}</div>`,
  ].join('');

  // Track which instance IDs we've already labelled within THIS render
  // pass so each card's name only appears once, on its first mention
  // (reading top-to-bottom, action then PV).
  const ctx: NameCtx = { seen: new Set(), instanceCardIds: report.instanceCardIds, catalog };

  const rows: string[] = [];
  const shown = report.consideredActions.slice(0, MAX_ACTIONS_SHOWN);
  for (let i = 0; i < shown.length; i++) {
    const c = shown[i];
    const isPicked = jsonEq(c.action, report.chosenAction);
    const actionStr = describeAction(c.action, ctx);
    const pvStr = c.principalVariation.slice(0, PV_HEAD).map(a => describeAction(a, ctx)).join(' → ');
    const tail = c.principalVariation.length > PV_HEAD ? ` … (+${c.principalVariation.length - PV_HEAD})` : '';
    rows.push(
      `<div style="padding:4px 6px;margin:2px 0;border-radius:3px;background:${isPicked ? '#1e3a4a' : 'transparent'};border-left:3px solid ${isPicked ? '#7fd' : 'transparent'}">`
        + `<div><span style="color:${isPicked ? '#7fd' : '#9af'}">${fmtScore(c.score).padStart(8)}</span>  ${escapeHtml(actionStr)}</div>`
        + (pvStr ? `<div style="opacity:0.65;padding-left:10ch">PV: ${escapeHtml(pvStr)}${tail}</div>` : '')
        + `</div>`,
    );
  }
  const more = report.consideredActions.length > MAX_ACTIONS_SHOWN
    ? `<div style="opacity:0.5;margin-top:4px">…${report.consideredActions.length - MAX_ACTIONS_SHOWN} more</div>`
    : '';

  container.innerHTML = header + rows.join('') + more;
}

interface NameCtx {
  seen: Set<string>;
  instanceCardIds: Record<string, string>;
  catalog: Map<string, CardDef>;
}

/** Render an instance id with its card name attached on first mention.
 *  Mutates `ctx.seen`. Subsequent mentions return only the bare id. */
function idWithName(instId: string, ctx: NameCtx): string {
  if (ctx.seen.has(instId)) return instId;
  ctx.seen.add(instId);
  const cardId = ctx.instanceCardIds[instId];
  if (!cardId) return instId;
  const def = ctx.catalog.get(cardId);
  return def ? `${instId} (${def.name})` : instId;
}

function describeAction(action: GameAction, ctx: NameCtx): string {
  switch (action.type) {
    case 'PLAY_CHARACTER':
      return `PLAY ${idWithName(action.instanceId, ctx)}→slot${action.slotIndex}`;
    case 'SUPPORT_CHARACTER':
      return `SUPPORT ${idWithName(action.handInstanceId, ctx)}→${idWithName(action.ringInstanceId, ctx)}`;
    case 'ACTIVATE_ABILITY':
      return `ACTIVATE ${idWithName(action.instanceId, ctx)}`;
    case 'PLAY_ACTION':
      return `PLAY_ACTION ${idWithName(action.instanceId, ctx)}`;
    case 'PLAY_REACTION':
      return `PLAY_REACTION ${idWithName(action.instanceId, ctx)}`;
    case 'DECLARE_ATTACKERS': {
      const parts = action.groups.map(g => {
        const ids = g.attackerIds.map(id => idWithName(id, ctx));
        return g.united && ids.length >= 2 ? `[u:${ids.join('+')}]` : ids.join('+');
      });
      return `ATTACK ${parts.join(', ')}`;
    }
    case 'ASSIGN_BLOCKER':
      return `BLOCK g${action.groupIndex}←${idWithName(action.blockerInstanceId, ctx)}`;
    case 'UNASSIGN_BLOCKER':
      return `UNBLOCK ${idWithName(action.blockerInstanceId, ctx)}`;
    case 'CONFIRM_BLOCKERS':
      return `CONFIRM_BLOCKERS`;
    case 'DISCARD_CARD':
      return `DISCARD ${idWithName(action.instanceId, ctx)}`;
    case 'SKIP_PHASE':
      return `SKIP_PHASE`;
    case 'ADVANCE_PHASE':
      return `ADVANCE_PHASE`;
  }
}

function fmtScore(n: number): string {
  if (!isFinite(n)) return n > 0 ? '+∞' : '-∞';
  if (Math.abs(n) > 1e6) return n.toExponential(1);
  return (n >= 0 ? '+' : '') + n.toFixed(1);
}

function jsonEq(a: unknown, b: unknown): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
}
